import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import StageShell from "@/components/StageShell";
import { useRun } from "@/hooks/useRun";
import { api, errMsg } from "@/lib/api";

function RedItem({ it, jobId, refresh }) {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState(it.text);
  const act = async (action, t) => {
    try { await api.post(`/jobs/${jobId}/factcheck/${it.id}`, { action, text: t }); toast.success("Saved"); setEdit(false); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const lineEditable = /^(bullet:|skills:|headline|summary)/.test(it.line_id);
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${it.resolution ? "border-slate-700 opacity-70" : "border-rose-500/50 bg-rose-500/5"}`} data-testid={`factcheck-item-${it.id}`}>
      <div className="flex items-center gap-2 text-xs">
        <XCircle size={14} className="text-rose-400" />
        <span className="uppercase text-rose-300 font-semibold">{it.kind}</span><span className="text-slate-200 text-sm">"{it.claim}"</span>
        <span className="font-mono text-slate-500">{it.line_id} · {it.checked_by}</span>
        {it.resolution && <span className="ml-auto text-emerald-300">{it.resolution}</span>}
      </div>
      {edit ? <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid={`factcheck-edit-text-${it.id}`} className="w-full bg-slate-900 border border-blue-500 rounded-md p-2 text-sm outline-none" />
        : <div className="text-sm text-slate-400">{it.text}</div>}
      {!it.resolution && (
        <div className="flex gap-2 flex-wrap">
          {lineEditable && (edit
            ? <button data-testid={`factcheck-save-${it.id}`} onClick={() => act("edit_line", text)} className="px-3 py-1 rounded-md bg-blue-600 text-xs">Save line</button>
            : <button data-testid={`factcheck-edit-${it.id}`} onClick={() => setEdit(true)} className="px-3 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-xs">Edit line</button>)}
          {it.line_id.startsWith("bullet:") && <button data-testid={`factcheck-remove-${it.id}`} onClick={() => act("remove_line")} className="px-3 py-1 rounded-md bg-slate-800 hover:bg-rose-600/60 text-xs">Remove bullet</button>}
          <button data-testid={`factcheck-confirm-${it.id}`} onClick={() => act("add_fact")} className="px-3 py-1 rounded-md bg-slate-800 hover:bg-emerald-700/60 text-xs">Client confirmed: add to FactSheet</button>
          {!lineEditable && <span className="text-[11px] text-muted-foreground self-center">Fix titles, dates and education in the Draft step (step 6), then re-run.</span>}
        </div>
      )}
    </div>
  );
}

export default function Stage9({ job, refresh }) {
  const runHook = useRun(job.id, 9, () => refresh());
  const fc = job.factcheck;
  const red = fc ? fc.items.filter((i) => !i.traced) : [];
  const green = fc ? fc.items.filter((i) => i.traced) : [];
  return (
    <StageShell job={job} stage={9} refresh={refresh} runHook={runHook}
      runs={[{ action: "fact_check", label: fc ? "Re-run fact check" : "Run fact check", testid: "factcheck-run-button" }]}>
      {!fc ? <div className="card-surface text-sm text-muted-foreground">Every number, tool, title, company and date must trace to the FactSheet or the original resume. Code checks numbers, dates and titles; AI checks tools and claims.</div> : (
        <div className="grid grid-cols-[minmax(0,1fr)_380px] gap-5 items-start">
          <div className="space-y-3" data-testid="factcheck-red-list">
            <div className="font-semibold">Untraceable <span className="text-rose-300">({red.filter((r) => !r.resolution).length} open)</span></div>
            {red.map((it) => <RedItem key={it.id} it={it} jobId={job.id} refresh={refresh} />)}
            {!red.length && <div className="card-surface text-emerald-300 text-sm">Everything traces to a source.</div>}
            {red.some((r) => r.resolution === "line edited" || r.resolution === "line removed") && <div className="text-xs text-amber-300">You changed the resume, so re-run the fact check before approving.</div>}
          </div>
          <div className="card-surface max-h-[800px] overflow-auto scroll-thin" data-testid="factcheck-traced-list">
            <div className="label-xs mb-2">Traced ({green.length})</div>
            {green.map((it) => (
              <div key={it.id} className="flex gap-2 text-xs py-1 border-t border-border first:border-0">
                <CheckCircle2 size={13} className="text-emerald-400 shrink-0" /><span className="text-slate-300">{it.claim}</span><span className="ml-auto text-slate-500 whitespace-nowrap">{it.kind} · {/^[0-9a-f-]{36}$/.test(it.source) ? `fact ${it.source.slice(0, 6)}` : it.source.slice(0, 24)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </StageShell>
  );
}
